/**
 * mediainfo-probe.js — MediaInfo-grade container/codec/color/HDR metadata for
 * ANY video (or audio) file, fully client-side, via the official MediaInfo
 * C++ library compiled to WASM (mediainfo.js, BSD-2).
 *
 * Why this exists:
 *   /analyze (the video-analyzer etalon) needs the authoritative technical
 *   read of a file the way broadcasters trust MediaInfo: real container,
 *   codec + profile + level, resolution + SAR/DAR, frame-rate + CFR/VFR,
 *   bit depth, chroma subsampling, colour primaries/transfer/matrix, HDR
 *   format (HDR10/HDR10+/HLG/Dolby Vision) + MaxCLL/FALL + mastering display,
 *   and EVERY audio + subtitle track with language. mp4box only covers the
 *   mp4 family and a few fields; WebCodecs only gives codec/dims. This is the
 *   single source of truth for "what is this file, really".
 *
 * Graceful degradation:
 *   The WASM (~2.6 MB) is lazy-loaded only on first probe and SW-cached. On
 *   devices where it cannot init (very old browsers, blocked WASM), the caller
 *   falls back to the lite path (__Mp4BoxProbe + WebCodecs.isSupported). This
 *   probe NEVER hangs the page — it rejects on timeout / load failure so the
 *   caller can degrade.
 *
 * Usage:
 *   const info = await window.__MediaInfoProbe(file, { slug:'analyze' });
 *   // → { ok, container, general, video:[...], audio:[...], text:[...],
 *   //     menu:[...], raw }  (each track is the raw MediaInfo object)
 *   // Tolerant field read across MediaInfo's spelling variants:
 *   const w = window.__MediaInfoProbe.field(info.video[0], 'Width');
 *
 * Reuse note: the lib URL + locateFile point at /vendor/mediainfo/ (self-host,
 * see public/vendor/download-all.sh). jsdelivr is the CDN fallback only.
 */
(function(){
'use strict';
if (window.__MediaInfoProbe && !window.__MediaInfoProbe.__isStub) return;

/* Asset routing (RU must NOT depend on external CDN — RKN blocks jsdelivr):
   the blade injects <meta name="mediainfo-base"> = AssetRouter->heavy(...):
     • RU   → '/vendor/mediainfo/'                      (Beget self-host)
     • intl → 'https://timbrica.com/vendor/mediainfo/'  (Beget cross-domain, 0 NL egress)
   Tier chain: that base → Beget absolute → jsdelivr (LAST; unreachable-but-harmless for RU). */
function metaBase(name, def) {
    try { var m = document.querySelector('meta[name="' + name + '"]'); if (m && m.content) return m.content; } catch (_) {}
    return def;
}
var WASM_DIR = metaBase('mediainfo-base', '/vendor/mediainfo/');
if (WASM_DIR.charAt(WASM_DIR.length - 1) !== '/') WASM_DIR += '/';
var SAME = '/vendor/mediainfo/';
var BEGET = 'https://timbrica.com/vendor/mediainfo/';
var CDN   = 'https://cdn.jsdelivr.net/npm/mediainfo.js@0.3.7/dist/';
/* Tiers: meta base (RU=local Beget / intl=Beget cross-domain) → same-origin
   (RU dup / intl=NL fallback / local=dev files) → Beget absolute → jsdelivr last. */
var LIB_TIERS  = [WASM_DIR + 'index.min.js', SAME + 'index.min.js', BEGET + 'index.min.js', CDN + 'umd/index.min.js'];
var WASM_TIERS = [WASM_DIR, SAME, BEGET, CDN];

var libLoadPromise = null;
function loadLib() {
    if (window.MediaInfo && typeof window.MediaInfo.mediaInfoFactory === 'function') return Promise.resolve(0);
    if (libLoadPromise) return libLoadPromise;
    libLoadPromise = new Promise(function(resolve, reject){
        (function tryTier(i){
            if (i >= LIB_TIERS.length) { reject(new Error('mediainfo.js failed to load')); return; }
            var s = document.createElement('script');
            s.src = LIB_TIERS[i];
            s.onload = function(){ resolve(i); };       // resolves the tier index that loaded
            s.onerror = function(){ tryTier(i + 1); };
            document.head.appendChild(s);
        })(0);
    });
    return libLoadPromise;
}

// One MediaInfo instance is kept alive (avoids recompiling the 2.6 MB module
// per re-analyze). The lib <script> loads without CORS, but the WASM fetch via
// locateFile IS cross-origin — so we walk the wasm-base tiers (Beget cross-domain
// → same-origin → jsdelivr) until the factory actually instantiates. This is
// what makes intl fall back from a CORS-blocked Beget wasm to its same-origin copy.
var miInstancePromise = null;
/* Размер чанка ЗАМЕРЕН, а не взят по умолчанию.
 *
 * MediaInfo протягивает через FileReader ВЕСЬ файл — для 199 МБ это 797
 * round-trip'ов по 256 КБ (умолчание библиотеки) и 1.26 с чистого
 * ввода-вывода. Цена здесь не в разборе, а в числе переходов JS↔wasm, то
 * есть в том же месте, что у `-c copy` (память
 * reference_wasm_copy_path_is_pure_io_2026_08_21).
 *
 * Замер на 199 МБ mp4, один браузер, один прогон:
 *     256 КБ — 1259 мс (797 чтений)      8 МБ —  639 мс (27)
 *       1 МБ —  700 мс (201)            16 МБ —  727 мс (15)
 *       4 МБ —  575 мс (52)             32 МБ —  808 мс (9)
 *
 * ⚠️ «Больше — лучше» здесь НЕВЕРНО: после 4 МБ выделение буфера начинает
 * стоить дороже сэкономленных переходов, и кривая идёт обратно вверх. 4 МБ
 * — измеренный минимум, а не круглое число.
 *
 * ⛔ `full: false` проверено и отвергнуто: на тех же файлах разницы нет
 * (2322 против 2411 мс), а поля те же — ручка в JS-сборке ни на что не
 * влияет. Полей не теряется ни одного ни при каком размере чанка
 * (проверено по 26 полям, которые страница реально показывает).
 */
var MI_CHUNK = 4 * 1024 * 1024;
function makeInstance(base) {
    return window.MediaInfo.mediaInfoFactory({
        format: 'object', full: true, chunkSize: MI_CHUNK,
        locateFile: function(path /*, prefix */){ return base + path; },
    });
}
function getInstance() {
    if (miInstancePromise) return miInstancePromise;
    miInstancePromise = (async function(){
        var lastErr = null;
        for (var i = 0; i < WASM_TIERS.length; i++) {
            try { return await makeInstance(WASM_TIERS[i]); }
            catch (e) { lastErr = e; }
        }
        miInstancePromise = null;   // allow retry on next call
        throw lastErr || new Error('mediainfo init failed (all wasm tiers)');
    })();
    return miInstancePromise;
}

function readChunk(file) {
    return function(chunkSize, offset) {
        return new Promise(function(resolve, reject){
            var r = new FileReader();
            r.onload = function(ev){
                if (ev.target.error) { reject(ev.target.error); return; }
                resolve(new Uint8Array(ev.target.result));
            };
            r.onerror = function(){ reject(r.error || new Error('read error')); };
            r.readAsArrayBuffer(file.slice(offset, offset + chunkSize));
        });
    };
}

function group(media) {
    var tracks = (media && media.track) || [];
    var out = { general: null, video: [], audio: [], text: [], menu: [], other: [] };
    for (var i = 0; i < tracks.length; i++) {
        var t = tracks[i], ty = String(t['@type'] || t.type || '').toLowerCase();
        if (ty === 'general') out.general = t;
        else if (ty === 'video') out.video.push(t);
        else if (ty === 'audio') out.audio.push(t);
        else if (ty === 'text') out.text.push(t);
        else if (ty === 'menu') out.menu.push(t);
        else out.other.push(t);
    }
    return out;
}

/* The single MediaInfo instance is NOT reentrant — serialize probes so two
   rapid uploads never interleave getSize/readChunk callbacks on one instance. */
var _miChain = Promise.resolve();
window.__MediaInfoProbe = function(file, opts) {
    var run = _miChain.then(function(){ return _probe(file, opts); }, function(){ return _probe(file, opts); });
    _miChain = run.catch(function(){});
    return run;
};
async function _probe(file, opts) {
    opts = opts || {};
    if (!file || typeof file.slice !== 'function') throw new Error('MediaInfoProbe: argument is not a Blob/File');

    await withTimeout(loadLib(), 20000, 'mediainfo lib load');
    var mi = await withTimeout(getInstance(), 30000, 'mediainfo init');

    var result = await withTimeout(
        mi.analyzeData(function(){ return file.size; }, readChunk(file)),
        45000, 'mediainfo analyze'
    );

    var media = result && result.media;
    if (!media || !media.track) throw new Error('mediainfo: empty result');
    var g = group(media);
    return {
        ok: true,
        container: field(g.general, 'Format') || '',
        general: g.general,
        video: g.video,
        audio: g.audio,
        text: g.text,
        menu: g.menu,
        other: g.other,
        raw: media,
    };
};

/* Tolerant field accessor — MediaInfo's object output uses inconsistent key
   spellings across versions/fields (Format_Profile vs format_profile,
   colour_primaries vs ColorPrimaries). Try each candidate, return first
   non-empty string. */
function field(track, /* ...names */) {
    if (!track) return '';
    for (var i = 1; i < arguments.length; i++) {
        var k = arguments[i];
        if (track[k] != null && track[k] !== '') return String(track[k]);
        // case-insensitive fallback
        for (var real in track) {
            if (track.hasOwnProperty(real) && real.toLowerCase() === k.toLowerCase()) {
                if (track[real] != null && track[real] !== '') return String(track[real]);
            }
        }
    }
    return '';
}
window.__MediaInfoProbe.field = field;

function withTimeout(p, ms, label) {
    return new Promise(function(resolve, reject){
        var done = false;
        var t = setTimeout(function(){ if (!done){ done = true; reject(new Error((label||'op') + ' timed out after ' + ms + 'ms')); } }, ms);
        Promise.resolve(p).then(function(v){ if (!done){ done = true; clearTimeout(t); resolve(v); } },
                                function(e){ if (!done){ done = true; clearTimeout(t); reject(e); } });
    });
}

})();

if (typeof window.__drainSharedFunction === 'function') window.__drainSharedFunction('__MediaInfoProbe');
